import { IntlMessageFormat } from "intl-messageformat";
import { useId, useMemo, useState } from "react";
import styles from "./IcuAssignmentField.module.css";

// Every rung this template binds, so the preview shows the pattern at each
// number rather than at one invented one. The whole argument for one template
// with a hole instead of six templates is that the single string stays correct
// at every rung — and plain substitution makes it correct at some and wrong at
// others, invisibly to whoever authored it.
export type PreviewBinding = {
  id: number;
  target: number | null;
  days: number | null;
};

type IcuAssignmentFieldProps = {
  name: string;
  label: string;
  localeCode: string;
  defaultValue: string;
  errors?: string[];
  bindings: readonly PreviewBinding[];
};

type Rendered =
  | { didRender: true; lines: { id: number; text: string }[] }
  | { didRender: false; message: string };

const render = (
  pattern: string,
  localeCode: string,
  bindings: readonly PreviewBinding[],
): Rendered => {
  if (pattern.trim() === "") {
    return { didRender: true, lines: [] };
  }

  try {
    const message = new IntlMessageFormat(pattern, localeCode);

    return {
      didRender: true,
      lines: bindings.map((binding) => ({
        id: binding.id,
        text: String(
          message.format({
            ...(binding.target === null ? {} : { target: binding.target }),
            ...(binding.days === null ? {} : { days: binding.days }),
          }),
        ),
      })),
    };
  } catch (error) {
    return {
      didRender: false,
      message: error instanceof Error ? error.message : String(error),
    };
  }
};

// A textarea rather than an input: the assignment is the whole task, and the
// constraints that make it distinctive ("use only emojis") are sentences of
// their own. Only the title is capped short.
export const IcuAssignmentField = ({
  name,
  label,
  localeCode,
  defaultValue,
  errors,
  bindings,
}: IcuAssignmentFieldProps) => {
  const id = useId();
  const errorId = `${id}-error`;
  const [pattern, setPattern] = useState(defaultValue);
  const isInvalid = errors !== undefined && errors.length > 0;

  const rendered = useMemo(
    () => render(pattern, localeCode, bindings),
    [pattern, localeCode, bindings],
  );

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        name={name}
        className={styles.input}
        rows={4}
        value={pattern}
        onChange={(event) => setPattern(event.target.value)}
        placeholder="Read {target, plural, one {# page} other {# pages}} of a book you have never opened."
        aria-invalid={isInvalid || !rendered.didRender || undefined}
        aria-describedby={isInvalid ? errorId : undefined}
      />
      <p className={styles.hint}>
        The task itself, imperative and complete: what to do and the rules of
        it. ICU MessageFormat, not plain substitution — English has two plural
        forms and Czech has four, so one wording gets two of them wrong unless
        the categories are spelled out. Never a restatement of the title.
      </p>
      {isInvalid ? (
        <p id={errorId} className={styles.error} role="alert">
          {errors.join(" ")}
        </p>
      ) : null}
      {rendered.didRender ? (
        <div className={styles.preview}>
          {bindings.length === 0 ? (
            <p className={styles.empty}>
              Add a rung to see this assignment rendered at each of its numbers.
            </p>
          ) : (
            <ul className={styles.lines}>
              {rendered.lines.map((line) => (
                <li key={line.id} className={styles.line}>
                  {line.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        // A malformed pattern throws at render time, in the user's app. Saying
        // so here is the only place it is cheap to fix.
        <p className={styles.parseError} role="alert">
          Not valid ICU MessageFormat: {rendered.message}
        </p>
      )}
    </div>
  );
};
